import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A pot-bellied witch's cauldron: a plump charcoal kettle on three stubby legs
// straddling a bed of glowing embers, filled to the brim with a bright green
// brew. Built to bubble — three named bubble spheres rise out of the brew and
// pop in a staggered loop while the brew surface pulses; "idle" simmers gently,
// "boil" runs the same business faster and taller. Storybook batch: plump
// masses, oversized rim and legs, warm saturated colours + cream trim.
//
// Frame: y = 0 is the ground, +Z is the front. Roughly 0.8 m tall over a
// 1x1 tile.

const POT = "#3a3e47";        // charcoal iron belly (never pure black)
const POT_DARK = "#2c2f36";   // deep charcoal shading (rim underside, legs)
const RIM = "#f6efe3";        // cream trim band around the mouth
const BREW = "#5ede4a";       // bright toy-green brew
const BREW_DEEP = "#3fae32";  // darker brew shade (bubbles read against it)
const EMBER = "#ff8c2e";      // glowing ember orange
const EMBER_HOT = "#ffc24d";  // hottest coals / glow core

// ── frame ────────────────────────────────────────────────────────────────────
const BELLY_R = 0.32;                 // pot belly radius
const BELLY_CY = 0.40;                // belly centre height
const MOUTH_R = 0.24;                 // opening radius
const MOUTH_Y = BELLY_CY + 0.24;      // rim/brew height (~0.64)
const BREW_Y = MOUTH_Y + 0.005;       // brew surface, a hair proud of the rim

// The animations breathe the brew and pop the bubbles about these anchors.
const BREW_PIVOT: [number, number, number] = [0, BREW_Y, 0];
const EMBER_PIVOT: [number, number, number] = [0, 0.05, 0];

// One stubby leg: a chunky tapered cylinder splayed out from under the belly,
// with a round paw-like foot so the pot reads planted, not spindly.
function leg(angle: number): Part[] {
  const x = Math.sin(angle) * 0.20;
  const z = Math.cos(angle) * 0.20;
  return [
    { geometry: new THREE.CylinderGeometry(0.05, 0.065, 0.20, 8), color: POT_DARK,
      position: [x, 0.11, z], rotation: [Math.cos(angle) * 0.28, 0, -Math.sin(angle) * 0.28] },
    { geometry: new THREE.SphereGeometry(0.055, 8, 6), color: POT_DARK,
      position: [x * 1.18, 0.045, z * 1.18], scale: [1, 0.7, 1] },
  ];
}

// One rising bubble, given its own name so each pops on its own beat. Authored
// at its mid-rise pose so the static preview already reads as "bubbling".
function bubble(name: string, x: number, z: number, r: number, lift: number): Part {
  return {
    geometry: new THREE.SphereGeometry(r, 8, 6), color: BREW_DEEP,
    position: [x, BREW_Y + lift, z], name,
  };
}

// A bubble's life in one loop: swell out of the brew, rise, pop to nothing,
// then hold under the surface until its next turn. `at` staggers the beat.
function bubbleTrack(target: string, at: number, dur: number, rise: number) {
  const t = (k: number) => (at + k * dur) % dur;
  // Keyframes must be in time order, so rebuild the cycle from phase `at`.
  const keys = [
    { k: 0.0, s: 0.05, y: 0 },          // dormant under the surface
    { k: 0.35, s: 1.0, y: 0.02 },       // swelled up, breaking the surface
    { k: 0.7, s: 1.15, y: rise },       // risen to full height
    { k: 0.78, s: 0.05, y: rise },      // pop!
    { k: 1.0, s: 0.05, y: 0 },          // sink home for the next beat
  ].map((p) => ({ t: t(p.k), s: p.s, y: p.y }))
    .sort((a, b) => a.t - b.t);
  return {
    target,
    pivot: [0, BREW_Y, 0] as [number, number, number],
    scale: keys.map((p) => ({ t: p.t, value: p.s, ease: "sine" as const })),
    position: keys.map((p) => ({ t: p.t, value: [0, p.y, 0] as [number, number, number], ease: "sine" as const })),
  };
}

// The shared bubbling business at a given tempo — idle simmers, boil rages.
function bubbling(dur: number, rise: number, brewSwell: number, emberSwell: number) {
  return [
    bubbleTrack("bubble-a", 0.0, dur, rise),
    bubbleTrack("bubble-b", dur * 0.38, dur, rise * 0.85),
    bubbleTrack("bubble-c", dur * 0.71, dur, rise * 0.7),
    // The brew surface breathes — a flat pulse, wider more than taller.
    {
      target: "brew", pivot: BREW_PIVOT,
      scale: [
        { t: 0, value: 1, ease: "sine" as const },
        { t: dur / 2, value: [brewSwell, 1.4, brewSwell] as [number, number, number], ease: "sine" as const },
        { t: dur, value: 1, ease: "sine" as const },
      ],
    },
    // The embers glow in counter-phase, like a fire being drawn.
    {
      target: "embers", pivot: EMBER_PIVOT,
      scale: [
        { t: 0, value: emberSwell, ease: "sine" as const },
        { t: dur / 2, value: 1, ease: "sine" as const },
        { t: dur, value: emberSwell, ease: "sine" as const },
      ],
    },
  ];
}

export const cauldron = defineModel({
  id: "cauldron",
  batchId: "mess",
  name: "Cauldron",
  tags: ["mess", "cute", "animated", "prop", "magic", "witch", "fantasy"],
  summary: "A pot-bellied charcoal cauldron of bubbling green brew over glowing embers.",
  description:
    "A plump toy-like witch's cauldron: a charcoal iron belly with a cream rim " +
    "band, three stubby paw-footed legs over a bed of glowing embers, and a " +
    "bright green brew whose named bubbles rise and pop in a staggered loop. " +
    "The idle animation simmers; the boil animation runs the same bubbles " +
    "faster and taller while the embers flare.",
  author: "models",
  footprint: 0.4,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // Ember bed: a low warm mound with three hot coals, named so it can flare.
      { geometry: new THREE.CylinderGeometry(0.17, 0.20, 0.06, 10), color: EMBER,
        position: [0, 0.03, 0], name: "embers" },
      { geometry: new THREE.IcosahedronGeometry(0.055, 0), color: EMBER_HOT,
        position: [0.07, 0.07, 0.04], name: "embers" },
      { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: EMBER_HOT,
        position: [-0.08, 0.065, 0.02], name: "embers" },
      { geometry: new THREE.IcosahedronGeometry(0.045, 0), color: EMBER_HOT,
        position: [0.0, 0.07, -0.08], name: "embers" },

      // Three stubby legs, splayed at thirds around the belly.
      ...leg(0), ...leg((Math.PI * 2) / 3), ...leg((Math.PI * 4) / 3),

      // The pot belly: a plump sphere squashed a touch, sitting over the coals.
      { geometry: new THREE.SphereGeometry(BELLY_R, 14, 10), color: POT,
        position: [0, BELLY_CY, 0], scale: [1, 0.92, 1] },
      // Neck up to the mouth.
      { geometry: new THREE.CylinderGeometry(MOUTH_R + 0.015, 0.285, 0.14, 14), color: POT,
        position: [0, BELLY_CY + 0.18, 0] },
      // Oversized rolled rim — the cream trim band that sells the storybook look.
      { geometry: new THREE.CylinderGeometry(MOUTH_R + 0.05, MOUTH_R + 0.05, 0.055, 14), color: RIM,
        position: [0, MOUTH_Y - 0.02, 0] },
      { geometry: new THREE.CylinderGeometry(MOUTH_R + 0.05, MOUTH_R + 0.02, 0.03, 14), color: POT_DARK,
        position: [0, MOUTH_Y - 0.06, 0] },
      // Two rivet dots on the front of the belly for toy detail.
      { geometry: new THREE.SphereGeometry(0.02, 6, 5), color: POT_DARK,
        position: [-0.09, BELLY_CY + 0.02, BELLY_R - 0.015] },
      { geometry: new THREE.SphereGeometry(0.02, 6, 5), color: POT_DARK,
        position: [0.09, BELLY_CY + 0.02, BELLY_R - 0.015] },

      // The brew surface — a bright green disc filling the mouth, named to pulse.
      { geometry: new THREE.CylinderGeometry(MOUTH_R, MOUTH_R, 0.03, 14), color: BREW,
        position: [0, BREW_Y, 0], name: "brew" },
      // A lazy drip rolled over the front rim.
      { geometry: new THREE.SphereGeometry(0.035, 8, 6), color: BREW,
        position: [0.10, MOUTH_Y - 0.045, MOUTH_R + 0.035], scale: [1, 1.5, 0.7] },

      // Three bubbles caught mid-rise (staggered heights for a lively still).
      bubble("bubble-a", -0.08, 0.05, 0.045, 0.05),
      bubble("bubble-b", 0.09, -0.03, 0.038, 0.10),
      bubble("bubble-c", 0.0, 0.11, 0.03, 0.02),
    ];
  },
  animations: [
    // 1. Idle — a gentle simmer: bubbles take slow turns rising and popping,
    //    the brew breathes, the embers glow softly in counter-phase.
    {
      id: "idle",
      label: "Simmer",
      duration: 3.6,
      loop: true,
      tracks: bubbling(3.6, 0.10, 1.05, 0.92),
    },
    // 2. Boil — the signature move: the same bubbles, twice the tempo and
    //    taller, the brew heaving and the embers flaring bright and big.
    {
      id: "boil",
      label: "Rolling boil",
      duration: 1.6,
      loop: true,
      tracks: bubbling(1.6, 0.16, 1.09, 0.8),
    },
  ],
});
